import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { isPast } from 'date-fns';
import { UrlObject } from 'url';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import Divider from '@components/Divider';
import LanguageContext from '@lib/contexts/languageContext';
import { Trans } from '@lingui/react';
import LoginModal from '@organisms/modals/LoginModal';
import { Button, Login } from './subcomponents';
import { CurrentUser, PlanTypeKind, SubscriptionStatus } from '@gql/generated';
import useFlags, { AvailableFlags } from '@lib/hooks/useFlags';
import { getCyclesTrans } from '@lib/constants';
import { StartTrialSubscriptionButton } from '@ecosystems/buttons/StartTrialSubscriptionButton';

const Menu = styled.div<{ open: boolean }>`
  position: absolute;
  top: 100%;
  right: 20px;
  display: flex;
  flex-direction: column;
  text-align: left;
  background-color: white;
  box-shadow: 0px 5px 5px -3px rgb(0 0 0 / 20%),
    0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%);
  opacity: 0;
  z-index: -1;
  pointer-events: none;
  transform: scale(0.7);
  transform-origin: top right;
  transition: opacity 331ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    transform 220ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  ${({ open }) => {
    return open
      ? `
        z-index: 999;
        opacity: 1;
        pointer-events: initial;
        transform: scale(1);
      `
      : '';
  }};
`;

const MenuItem = styled(Link)`
  min-width: 230px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.black};
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
  padding: 20px 30px;

  :hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const HeaderMenuItem = styled(MenuItem)`
  justify-content: space-between;
  padding: 20px 30px;
  background-color: white;
  cursor: pointer;
`;

const FooterMenuItem = styled(HeaderMenuItem)`
  padding: 18px 30px;
`;

const UpIcon = styled(KeyboardArrowUp)<{ open: boolean }>`
  && {
    max-width: 24px;
    max-height: 24px;
    color: ${({ theme }) => theme.colors.primary['blue-1']};
    transform: rotate3d(1, 0, 0, 180deg);
    transition: transform 500ms ease;
    ${props => (props.open ? 'transform: rotate3d(1, 0, 0, 0deg);' : '')}
  }
`;

const BlueLabel = styled.span`
  /* &&& increases specificity because StyledMenuItem sets color differently */
  &&& {
    width: auto;
    color: ${({ theme }) => theme.colors.primary['blue-1']};
    font-weight: 600;
    cursor: pointer;
  }
`;

const PlanLabel = styled.span`
  margin-right: 20px;
`;

const NewTag = styled.span`
  font-size: 11px;
  color: #fff;
  font-weight: 500;
  border-radius: 3px;
  padding: 5px;
  margin-left: 8px;
  background-color: rgb(18, 158, 220);
`;

const MenuDropdown = ({ locale, onItemClick }) => {
  const [flags, { loading }] = useFlags();

  const links = [
    {
      link: `/${locale}/me/dashboard`,
      text: <Trans id="userlogin.my_dashboard" />,
    },
    {
      link: `/${locale}/me/favorites/videos`,
      text: <Trans id="userlogin.my_favorites" />,
    },
    {
      link: `/${locale}/me/playlists`,
      text: <Trans id="userlogin.my_playlists" />,
    },
    {
      link: `/${locale}/me/settings`,
      text: <Trans id="userlogin.settings" />,
    },
    {
      link: `/${locale}/me/settings/subscriptions`,
      text: <Trans id="userlogin.my_subscription" />,
    },
  ];

  if (!flags.disable_dashboard_stats && !loading) {
    links.push({
      link: `/${locale}/me/statistics`,
      text: (
        <>
          <Trans id="userlogin.my_stats" />
          <NewTag>New</NewTag>
        </>
      ),
    });
  }

  return (
    <>
      {links.map((item, i) => {
        return (
          <MenuItem key={i} href={item.link} onClick={onItemClick}>
            {item.text}
          </MenuItem>
        );
      })}
    </>
  );
};

type LinkType = {
  link: string | UrlObject;
  text: React.ReactNode;
};

const getHeaderItems = (
  currentUser: CurrentUser,
  locale,
  flags: AvailableFlags
) => {
  if (!currentUser?.id) {
    return [];
  }

  const accessUntil = currentUser.accessUntil;
  const hasWellness = currentUser?.hasWellness;
  const hasAccess = accessUntil ? !isPast(new Date(accessUntil)) : true;

  let link: React.ReactElement | LinkType = {
    link: `/${locale}/me/settings/subscriptions`,
    text: <Trans id="userlogin.menu.manage" />,
  };

  let text;
  if (hasWellness && hasAccess) {
    text = <Trans id="userlogin.menu.free_premium" />;
    return [text, link];
  } else if (
    currentUser?.subscription?.currentSubscriptionPlan?.planType.includes(
      'trial'
    ) ||
    currentUser?.subscription?.currentSubscriptionPlan?.planType ===
      'free_plan' ||
    currentUser?.subscription?.inTrialPeriod
  ) {
    // wellness trial
    if (
      currentUser?.subscription?.trial?.subscriptionPlanType ===
      PlanTypeKind['Wellness']
    ) {
      text = <Trans id="userlogin.menu.wellness_trial" />;
      link.text = null;
      link.link = '#';
    } else if (
      [
        PlanTypeKind['Quarterly'],
        PlanTypeKind['Yearly'],
        PlanTypeKind['Monthly'],
      ].includes(currentUser?.subscription?.trial?.subscriptionPlanType)
    ) {
      text = (
        <Trans
          id="userlogin.menu.trial_plan"
          values={{
            cycle: getCyclesTrans()[
              currentUser?.subscription?.trial?.subscriptionPlanType
            ],
          }}
        />
      );
      link = (
        <StartTrialSubscriptionButton link>
          <Trans id="userlogin.menu.activate_subscription_now" />
        </StartTrialSubscriptionButton>
      );
    } else if (
      // if user has premium without trial or subscription nor wellness
      // using access code or direct premium assignement from admin
      hasAccess &&
      currentUser?.subscription?.currentSubscriptionPlan?.planType ===
        'free_plan'
    ) {
      text = <Trans id="userlogin.menu.free_plan" />;
      link.link = `/${locale}/subscription/select`;
      link.text = <Trans id="userlogin.menu.upgrade" />;
    } else if (currentUser?.trial) {
      text = <Trans id="userlogin.menu.free_plan" />;
      if (flags?.enable_manual_trial) {
        link.link = `/${locale}/trial/select`;
        link.text = <Trans id="userlogin.menu.start_trial" />;
      }
    } else if (hasAccess) {
      text = <Trans id="userlogin.menu.free_trial" />;
      link.text = null;
      link.link = '#';
    }

    return [text, link];
  } else {
    const previousPlan =
      currentUser?.subscription?.currentSubscriptionPlan?.planType;
    if (!hasAccess) {
      text = <Trans id="userlogin.menu.expired_plan" />;
      link.text = <Trans id="userlogin.menu.renew" />;
      // only send to payment page if user has plan already
      if (
        ['monthly', 'yearly', 'quarterly'].includes(
          currentUser?.subscription?.currentSubscriptionPlan?.planType
        )
      ) {
        link.link = {
          pathname: `/${locale}/subscription/select`,
          query: { paymentType: previousPlan },
        };
      }
    } else if (
      currentUser?.subscription?.status === SubscriptionStatus['Cancelled']
    ) {
      text = <Trans id="userlogin.menu.cancelled_plan" />;
      link.text = <Trans id="userlogin.menu.renew" />;
      link.link = {
        query: { paymentType: previousPlan },
        pathname: `/${locale}/subscription/select`,
      };
    } else {
      if (
        currentUser?.subscription?.currentSubscriptionPlan?.planType.includes(
          'monthly'
        )
      ) {
        text = <Trans id="userlogin.menu.monthly_plan" />;
      } else if (
        currentUser?.subscription?.currentSubscriptionPlan?.planType.includes(
          'quarterly'
        )
      ) {
        text = <Trans id="userlogin.menu.quarterly_plan" />;
      } else if (
        currentUser?.subscription?.currentSubscriptionPlan?.planType.includes(
          'yearly'
        )
      ) {
        text = <Trans id="userlogin.menu.yearly_plan" />;
      } else {
        text = <Trans id="userlogin.menu.premium_plan" />;
      }
    }

    return [text, link];
  }
};

const UserLogin = props => {
  const router = useRouter();
  const locale = useContext(LanguageContext);
  const anchorEl = useRef<HTMLDivElement>(null);
  const [flags] = useFlags();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (router.query.action === 'login') {
      props.openLoginModal();
    }
    // we abserve query.action because in test mode we do frontend redirect
  }, [router.query.action]);

  const handleClick = () => {
    setOpen(!open);
    if (open) {
      props.closeMenuLayer();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleGlobalClick = useCallback(
    (e: MouseEvent) => {
      setOpen(open => {
        if (open && !anchorEl.current.contains(e.target as HTMLElement)) {
          return false;
        }

        return open;
      });
    },
    [open, anchorEl.current]
  );

  useEffect(() => {
    window.addEventListener('click', handleGlobalClick);

    return () => {
      setOpen(false);
      window.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  const { isAuthenticated, currentUser } = props;

  const [planLabel, actionButton] = getHeaderItems(currentUser, locale, flags);

  return isAuthenticated ? (
    <div ref={anchorEl}>
      <Login.AvatarWrapper onClick={handleClick}>
        {currentUser ? (
          <span className="Avatar--Name">
            {currentUser.firstName
              ? currentUser.firstName
              : currentUser.profile?.displayName}
          </span>
        ) : null}

        <Login.Avatar
          src={
            currentUser?.assets?.avatar?.includes?.('missing.png')
              ? '/static/profile-placeholder.svg'
              : currentUser?.assets?.avatar
          }
        />
        <UpIcon open={open} />
      </Login.AvatarWrapper>
      <Menu open={open}>
        {/* here we render the menu header */}
        {actionButton ? (
          React.isValidElement(actionButton) ? (
            <HeaderMenuItem as="div">
              <PlanLabel>{planLabel}</PlanLabel>
              {actionButton}
            </HeaderMenuItem>
          ) : (
            <HeaderMenuItem
              href={actionButton?.link ?? '#'}
              onClick={handleClose}
            >
              <PlanLabel>{planLabel}</PlanLabel>
              <BlueLabel>{actionButton.text}</BlueLabel>
            </HeaderMenuItem>
          )
        ) : null}
        <Divider />
        <MenuDropdown locale={locale} onItemClick={handleClick} />
        <Divider />
        <FooterMenuItem
          href="/logout"
          onClick={e => {
            e.preventDefault();
            window.location.href = '/logout';
          }}
        >
          <Trans id="userlogin.logout" message="Log out" />
        </FooterMenuItem>
      </Menu>
    </div>
  ) : (
    <Login.Wrapper>
      <Button
        className="loginCTA"
        href={{
          pathname: `/${locale}/login`,
          query: {
            to: router?.asPath,
          },
        }}
        appearance="dark"
        link
        size="small"
        onClick={e => {
          e.preventDefault();
          props.openLoginModal();
        }}
      >
        <Trans id="header.login" message="login" />
      </Button>
      <Button
        className="register"
        href={{
          pathname: `/${locale}/register/account`,
          query: {
            ...(router?.query?.code ? { code: router?.query?.code } : {}),
          },
        }}
        appearance="default-blue"
        size="small"
      >
        <Trans id="header.try_free" message="Try for free" />
      </Button>
    </Login.Wrapper>
  );
};

const WithLogin = props => {
  const router = useRouter();

  return (
    <LoginModal
      onClose={() => {
        if (router.query.to || router.query.action) {
          // lang, is baked ino the url, so we exclude it from url re-build
          // eslint-disable-next-line no-unused-vars
          const { action, to, lang, ...query } = router.query;

          // let queryString = Object.entries(query)
          //   .map(entry => `${entry[0]}=${entry[1]}`)
          //   .join('&');
          // if (queryString) {
          //   queryString = `?` + queryString;
          // }
          const asPath = router.asPath.split('?')[0];
          setTimeout(() => {
            router.replace(router.pathname, {
              pathname: asPath,
              query,
            });
          });
        }
      }}
    >
      {(openModal, closeModal) => (
        <UserLogin
          {...props}
          openLoginModal={openModal}
          closeLoginModal={closeModal}
        />
      )}
    </LoginModal>
  );
};

export default WithLogin;
